import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth-context.js";
import { fmtAge } from "../components/substation/bands.js";
/*
 * O CONTRATO VEM DE UM LUGAR SÓ — aqui havia a SEGUNDA das cinco cópias.
 *
 * Esta declaração tinha `serial` e `nominalPowerKw` e NÃO tinha `location`,
 * `label` nem `garageId` — divergindo da cópia do `ChargersPage` em campos
 * diferentes. E tinha `lastHeartbeatAt` (removido do contrato) e `serial`
 * (renomeado para `serialNumber`), dois nomes que o `tsc` não podia cobrar
 * porque o `r.json()` é `any` e a anotação sobre `any` é afirmação, não
 * verificação.
 */
import {
  contatoApresentado,
  configuracaoDeCarregadores,
  erroDeFonteNaoSuportada,
  fabricanteEModelo,
  fonteForneceCapacidade,
  fraseDeFalhaDeComando,
  fraseDeRelacaoVazia,
  idadeCurtaDoContato,
  legendaDeConectoresDegradados,
  motivoDeComandoIndisponivel,
  nomeDoCarregador,
  ROTULO_DO_MEIO_DE_CONEXAO,
  type RespostaDeCarregador,
} from "./chargers-contrato.js";

/*
  NÃO HÁ ALIAS LOCAL AQUI, e a ausência é deliberada.

  Esta tela deixou de nomear a linha de carregador: ela lê `envelope.charger`,
  cujo tipo é `RespostaDeCarregador['charger']` — ou seja, `LinhaDeCarregador` de
  `@nxvolt/shared`, por inferência. Um `type Charger = …` aqui seria um nome
  local para o mesmo tipo, e é exatamente o começo do caminho que produziu cinco
  declarações divergentes nesta base: primeiro vem o alias, depois alguém
  "acrescenta um campo que falta" nele.
*/

interface Tx {
  id: number;
  ocppTransactionId: number;
  cpId: string;
  connectorId: number;
  idTag: string;
  startTime: string;
  stopTime: string | null;
  energyWh: number | null;
  stopReason: string | null;
}

interface MeterValue {
  id: number;
  timestamp: string;
  valueWh: number;
  powerW: number | null;
  soc: number | null;
}

function fmt(iso: string | null) {
  return iso
    ? new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";
}

function statusBadge(status: string) {
  const cls = ["nx-badge"];
  if (["Charging", "Preparing", "Finishing"].includes(status))
    cls.push("nx-badge--charging");
  else if (status === "Available") cls.push("nx-badge--available");
  else if (status === "Faulted" || status === "Unavailable")
    cls.push("nx-badge--fault");
  else cls.push("nx-badge--neutral");
  return <span className={cls.join(" ")}>{status}</span>;
}

/** SVG chart de linha simples (sem lib externa). */
function LineChart({ mvs }: { mvs: MeterValue[] }) {
  if (mvs.length < 2)
    return (
      <p style={{ color: "var(--nx-mute)" }}>Poucas medições para plotar.</p>
    );
  const width = 640;
  const height = 200;
  const pad = { l: 48, r: 12, t: 12, b: 28 };
  const w = width - pad.l - pad.r;
  const h = height - pad.t - pad.b;

  const t0 = new Date(mvs[0]!.timestamp).getTime();
  const t1 = new Date(mvs[mvs.length - 1]!.timestamp).getTime();
  const y0 = Math.min(...mvs.map((m) => m.valueWh));
  const y1 = Math.max(...mvs.map((m) => m.valueWh));
  const yRange = Math.max(y1 - y0, 1);
  const tRange = Math.max(t1 - t0, 1);

  const points = mvs.map((m) => {
    const x = pad.l + ((new Date(m.timestamp).getTime() - t0) / tRange) * w;
    const y = pad.t + h - ((m.valueWh - y0) / yRange) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height="220"
      role="img"
      aria-label="Energia acumulada"
    >
      <line
        x1={pad.l}
        y1={pad.t + h}
        x2={pad.l + w}
        y2={pad.t + h}
        stroke="var(--nx-line)"
      />
      <line
        x1={pad.l}
        y1={pad.t}
        x2={pad.l}
        y2={pad.t + h}
        stroke="var(--nx-line)"
      />
      <text
        x={pad.l - 6}
        y={pad.t + 10}
        textAnchor="end"
        fontSize="10"
        fill="var(--nx-mute)"
      >
        {(y1 / 1000).toFixed(1)} kWh
      </text>
      <text
        x={pad.l - 6}
        y={pad.t + h}
        textAnchor="end"
        fontSize="10"
        fill="var(--nx-mute)"
      >
        {(y0 / 1000).toFixed(1)} kWh
      </text>
      <polyline
        fill="none"
        stroke="var(--nx-orange)"
        strokeWidth="2"
        points={points.join(" ")}
      />
      {mvs.map((m, i) => {
        const x = pad.l + ((new Date(m.timestamp).getTime() - t0) / tRange) * w;
        const y = pad.t + h - ((m.valueWh - y0) / yRange) * h;
        return (
          <circle
            key={m.id}
            cx={x}
            cy={y}
            r={i === mvs.length - 1 ? 4 : 3}
            fill="var(--nx-orange)"
          />
        );
      })}
    </svg>
  );
}

export function ChargerDetailPage({ cpId }: { cpId: string }) {
  const { user } = useAuth();
  const canOperate = user?.role === "admin" || user?.role === "operator";
  /* O ENVELOPE INTEIRO: `/api/chargers/:cpId` virou
     `{ charger, sources, configuration, generatedAt }`, e `configuration`
     carrega o limiar de frescor que é do SERVIDOR. Guardar só `charger` jogava
     fora o limiar e obrigaria esta tela a inventar o seu. */
  const [envelope, setEnvelope] = useState<RespostaDeCarregador | null>(null);
  const [txs, setTxs] = useState<Tx[]>([]);
  const [selectedTx, setSelectedTx] = useState<number | null>(null);
  const [mvs, setMvs] = useState<MeterValue[]>([]);
  /* A impossibilidade de `meter_values`, quando a rota a devolve em 409 — ver
     o `useEffect` das leituras. Não é erro: é causa. */
  const [mvsImpossivel, setMvsImpossivel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [falha, setFalha] = useState<string | null>(null);
  const [savingConfiguration, setSavingConfiguration] = useState(false);

  const load = useCallback(() => {
    fetch(`/api/chargers/${encodeURIComponent(cpId)}`, {
      credentials: "include",
    })
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error(String(r.status))),
      )
      .then((d: RespostaDeCarregador) => setEnvelope(d))
      .catch((e: Error) => setError(e.message));

    fetch(`/api/transactions?cpId=${encodeURIComponent(cpId)}&limit=20`, {
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : { transactions: [] }))
      .then((d: { transactions: Tx[] }) => {
        setTxs(d.transactions);
        if (d.transactions[0] && selectedTx == null)
          setSelectedTx(d.transactions[0].ocppTransactionId);
      });
  }, [cpId, selectedTx]);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (selectedTx == null) return;
    let vivo = true;
    void (async () => {
      const r = await fetch(`/api/transactions/${selectedTx}/meter-values`, {
        credentials: "include",
      });
      if (!vivo) return;
      if (r.ok) {
        const d = (await r.json()) as { meterValues: MeterValue[] };
        setMvs(d.meterValues ?? []);
        setMvsImpossivel(null);
        return;
      }
      /*
        O 409 DE `meter_values` É IMPOSSIBILIDADE, não "sem leituras".

        `public.meter_values` só é escrita pelo servidor OCPP DESTE portal; os
        carregadores de origem mandam as suas ao servidor de lá. Um gráfico
        vazio com "poucas medições para plotar" promete que esperar resolve, e
        não resolve. A mensagem vem pronta do servidor porque é afirmação sobre
        a topologia de fontes.
      */
      const corpo = (await r.json().catch(() => null)) as unknown;
      if (!vivo) return;
      setMvs([]);
      const impossivel = erroDeFonteNaoSuportada(corpo);
      setMvsImpossivel(impossivel?.message ?? null);
    })();
    return () => {
      vivo = false;
    };
  }, [selectedTx]);

  /*
    Um caminho só para os quatro comandos, com o 409 tratado como CAUSA.

    O `alert(data?.error ?? "Erro 409")` imprimia `source_unsupported` — o
    código interno — e descartava `message`, que é a única frase que explica
    por que a ação é impossível. Agora a frase vai para uma região
    `role="status"` perto dos botões: o modal nativo tira o foco, não é lido
    junto do que explica e some sem deixar rastro.
  */
  async function enviar(
    path: "remote-start" | "remote-stop" | "reset" | "unlock",
    body: Record<string, unknown>,
    marca: string,
  ) {
    setBusy(marca);
    setFalha(null);
    try {
      const response = await fetch(`/api/chargers/${encodeURIComponent(cpId)}/${path}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        load();
        return;
      }
      const corpo = (await response.json().catch(() => null)) as unknown;
      setFalha(fraseDeFalhaDeComando(response.status, corpo));
      if (erroDeFonteNaoSuportada(corpo) !== null) load();
    } finally {
      setBusy(null);
    }
  }

  async function remoteStop(txId: number) {
    if (!confirm(`Parar transação ${txId}?`)) return;
    await enviar("remote-stop", { transactionId: txId }, "remote-stop");
  }

  async function command(path: "remote-start" | "reset" | "unlock", body: Record<string, unknown>, label: string) {
    if (!confirm(`${label} em ${cpId}?`)) return;
    await enviar(path, body, path);
  }

  async function saveConfiguration(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const value = String(form.get("nominalPowerKw") ?? "").trim();
    const nominalPowerKw = value === "" ? null : Number(value);
    if (nominalPowerKw !== null && (!Number.isInteger(nominalPowerKw) || nominalPowerKw < 1 || nominalPowerKw > 2000)) {
      alert("Informe uma potência nominal inteira entre 1 e 2000 kW.");
      return;
    }

    setSavingConfiguration(true);
    setFalha(null);
    try {
      const response = await fetch(`/api/chargers/${encodeURIComponent(cpId)}/configuration`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nominalPowerKw }),
      });
      if (!response.ok) {
        const corpo = (await response.json().catch(() => null)) as unknown;
        /*
          ESTA ROTA **NÃO TEM** 409 `SOURCE_UNSUPPORTED`, e é decisão explícita
          do PM: ela não consulta fonte nenhuma, grava DECLARAÇÃO DO OPERADOR
          numa coluna local. `configuration` continua em
          `unsupportedFields` da fonte — mas aquilo é Get/ChangeConfiguration
          sobre WebSocket OCPP, outra coisa com o mesmo nome. Tratar uma falha
          daqui com o vocabulário de campo-não-suportado seria trocar um estado
          por outro, que é a família que esta fase persegue.
          `fraseDeFalhaDeComando` cobre 403/404/503/resto; se um 409 aparecer
          aqui, ele é bug do servidor e a frase genérica é a resposta honesta.
        */
        setFalha(fraseDeFalhaDeComando(response.status, corpo));
        return;
      }
      /*
        RECARREGA O ENVELOPE em vez de aproveitar o corpo da resposta.

        O `PATCH` devolve `{ charger: <linha de public.chargers> }` — a linha
        CRUA da tabela, não `LinhaDeCarregador`: sem `contact`, sem
        `sourceLabel`, sem `unsupportedFields`, e COM `lastHeartbeatAt`.
        Costurá-la no estado reintroduziria, pela porta dos fundos, exatamente a
        forma antiga que esta entrega remove — e o `contact` ausente derrubaria
        o render no próximo quadro. Medido contra o servidor em 31/10/2026.
      */
      load();
    } finally {
      setSavingConfiguration(false);
    }
  }

  const charger = envelope?.charger ?? null;
  if (error && !charger) return <div className="nx-error">Erro: {error}</div>;
  if (!charger) return <div className="nx-loader">Carregando…</div>;

  /* O limiar vem do SERVIDOR (`configuration.staleAfterSeconds`). Sem ele não
     se classifica frescor — hardcodar seria a quinta grafia do mesmo número. */
  const cfg = configuracaoDeCarregadores(envelope?.configuration.staleAfterSeconds ?? 0);
  /*
    "ÚLTIMA COMUNICAÇÃO" SAIU — e a razão é que ela era duas afirmações fundidas.

    Era `charger.lastHeartbeatAt ?? charger.lastBootAt`: um campo que o contrato
    removeu, com fallback num campo que a fonte de origem declara IMPOSSÍVEL
    (`last_boot_at` vem `null` para ela). O resultado media "—" para todos os
    equipamentos sincronizados, e para os de OCPP direto misturava heartbeat com
    boot numa frase só. Agora é `contact`, com `means` grudado no número.
  */
  const contato = contatoApresentado(charger, cfg, fmtAge);
  /*
    O MESMO `semSinalDe` DAS OUTRAS TRÊS TELAS.

    Esta era a QUARTA cópia do predicado — ela sobreviveu à rodada que apagou as
    outras três, porque eu consertei as ocorrências que o revisor nomeou em vez
    de varrer a classe. É literalmente o defeito de método que a revisão
    apontou, cometido dentro do conserto dele.

    Como as outras, esta cópia não cobria `sem-leitura`: o `temLimiar` local
    também sumiu, porque a guarda do limiar mora no predicado.
  */
  const semSinal = contato.semSinal;
  const motivoSemComando = motivoDeComandoIndisponivel(charger);
  const legendaDegradada = semSinal
    ? legendaDeConectoresDegradados(charger, cfg)
    : null;
  const nomeExibido = nomeDoCarregador(charger);
  /* Os bocais FÍSICOS. O `0` é o ponto de recarga, não um conector —
     mesma regra das três telas irmãs e das listas de comando abaixo. */
  const conectoresFisicos = charger.connectors.filter((k) => k.connectorId > 0);

  return (
    <div>
      <p style={{ color: "var(--nx-mute)", fontSize: ".875rem" }}>
        <Link to="/app/chargers">← Carregadores</Link>
      </p>
      <div className="nx-page-head">
        <div className="nx-page-head__title">
          <span className="nx-page-head__eyebrow">Detalhe do carregador</span>
          <h1>
            <span className={nomeExibido === charger.cpId ? "mono" : undefined}>{nomeExibido}</span>
            {/*
              O BADGE DIZ O QUE `connected` AFIRMA PARA ESTA FONTE.

              `connected` tem DOIS significados neste banco com o mesmo nome:
              em OCPP direto é "há WebSocket com este portal"; na fonte
              sincronizada é o estado que o portal de origem mantém. Imprimir
              "online" nos dois casos é apresentar como conexão algo que
              ninguém mediu daqui. O `title` carrega a afirmação exata.
            */}
            {/*
              E ELE DEGRADA COM O CONTATO — mesma regra da lista.

              BUG-SANITY: `connected = true` com carimbo 2,9 h NO FUTURO
              imprimia "online" em verde. Os dois fatos são compatíveis entre si
              (a fonte diz que o equipamento está ativo) e a leitura não é: a
              única evidência temporal que existe não pode ser situada no tempo.
            */}
            <span
              className={
                semSinal ? "nx-badge nx-badge--neutral" : "nx-badge nx-badge--available"
              }
              title={contato.fraseDeConexao}
            >
              {/* O MESMO `Record` exaustivo das outras telas: "nunca reportou"
                  ≠ "sem sinal" ≠ "sem conexão" ≠ "carimbo à frente". */}
              {contato.rotuloSemSinal ?? "online"}{" "}
              <small style={{ fontWeight: 400 }}>
                ({ROTULO_DO_MEIO_DE_CONEXAO[charger.connectedMeans].curto})
              </small>
            </span>
          </h1>
          {nomeExibido !== charger.cpId && (
            <div className="nx-page-head__sub mono">{charger.cpId}</div>
          )}
          <div className="nx-page-head__sub">
            Fonte: {charger.sourceLabel}
          </div>
        </div>
      </div>

      <div className="nx-kpi-grid">
        <div className="nx-kpi nx-kpi--navy">
          <div className="nx-kpi__label">Fabricante / Modelo</div>
          {/*
            O APELIDO DE EXIBIÇÃO VALE AQUI TAMBÉM.

            Este cartão imprimia `telluspower` cru enquanto a lista de
            Carregadores imprimia `Nansen` para o MESMO equipamento — duas telas
            do mesmo portal discordando sobre o nome do fornecedor. O `title`
            carrega o valor do cadastro e do log, que é onde o custo do apelido
            está registrado.
          */}
          <div
            className="nx-kpi__value"
            style={{ fontSize: "1.2rem" }}
            title={fabricanteEModelo(charger).titulo}
          >
            {fabricanteEModelo(charger).texto}
          </div>
        </div>
        <div className="nx-kpi nx-kpi--cyan">
          <div className="nx-kpi__label">Firmware / Série</div>
          <div className="nx-kpi__value mono" style={{ fontSize: "1.1rem" }}>
            {/* AUSÊNCIA e não impossibilidade: `firmware` NÃO está em
                `unsupportedFields` da fonte sincronizada — ela fornece o campo e
                a maioria dos equipamentos simplesmente não o informa. É o par
                que testa o classificador: `lastBootAt` abaixo vem `null` por
                IMPOSSIBILIDADE, e as duas frases têm de ser diferentes. */}
            {charger.firmware ?? "não informado"}
            <br />
            <span
              style={{ fontSize: ".85rem" }}
              title={
                charger.source === "vgpl"
                  ? "Nos equipamentos sincronizados da origem este valor é sintetizado lá (NSN- + id), não é número de série de fábrica."
                  : undefined
              }
            >
              {charger.serialNumber ?? "série não informada"}
            </span>
          </div>
        </div>
        <div className="nx-kpi nx-kpi--orange">
          {/*
            O KPI ERA "Último heartbeat" sobre `lastHeartbeatAt` — campo que o
            contrato REMOVEU. Medido antes do conserto: imprimia "—" para todos
            os carregadores, inclusive os que têm heartbeat de verdade. Agora o
            rótulo é decidido por `contact.means`, e a idade é a ASSINADA.
          */}
          <div className="nx-kpi__label">{contato.rotulo}</div>
          {/*
            NO CARTÃO VAI O NÚMERO; A FRASE VAI PARA A FAIXA ABAIXO.

            Medido na tela: a frase completa (180 caracteres) dentro de
            `nx-kpi__value` quebrava UMA PALAVRA POR LINHA num cartão de 300 px
            de altura. O cartão é dimensionado para grandeza curta — a frase
            precisa de largura, e ela existe logo abaixo.
          */}
          <div
            className="nx-kpi__value"
            style={{
              fontSize: "1.2rem",
              filter: contato.degradado ? "grayscale(0.6)" : undefined,
            }}
            title={contato.rotulo}
          >
            {idadeCurtaDoContato(charger.contact, fmtAge)}
          </div>
        </div>
        <div className="nx-kpi nx-kpi--navy">
          <div className="nx-kpi__label">Potência nominal / Último boot</div>
          <div className="nx-kpi__value" style={{ fontSize: "1.1rem" }}>
            {charger.nominalPowerKw == null ? "não informada" : `${charger.nominalPowerKw} kW`}
            <br />
            {/* IMPOSSIBILIDADE, e a frase é diferente da de ausência logo
                acima: a fonte declara `last_boot_at` não fornecido, e a view o
                ANULA. Esperar não resolve. A frase longa (com a fonte nomeada)
                vai para o `title`: no cartão ela quebrava uma palavra por
                linha, e o cartão é dimensionado para grandeza curta. */}
            <span
              style={{ fontSize: ".85rem" }}
              title={
                fonteForneceCapacidade(charger, "last_boot_at")
                  ? undefined
                  : `O último boot não é fornecido por ${charger.sourceLabel}: esta fonte não publica o campo, e esperar não resolve.`
              }
            >
              {fonteForneceCapacidade(charger, "last_boot_at")
                ? `boot: ${fmt(charger.lastBootAt)}`
                : "boot não fornecido"}
            </span>
          </div>
        </div>
      </div>

      {/*
        A FAIXA DE CONTATO — onde a frase tem largura para ser lida.

        Ela carrega as três coisas que o número sozinho não diz: o que o carimbo
        MEDE (`means`, já embutido na frase), quantos equipamentos compartilham
        ESTE carimbo, e o que `connected` afirma para esta fonte.
      */}
      <div
        className="nx-card"
        style={{ marginTop: "var(--nx-space-4)", display: "grid", gap: 6 }}
      >
        <p
          style={{
            margin: 0,
            filter: contato.degradado ? "grayscale(0.6)" : undefined,
          }}
        >
          <strong>{contato.rotulo}:</strong> {idadeCurtaDoContato(charger.contact, fmtAge)}
        </p>
        {contato.fraseDeCompartilhamento !== null && (
          <p style={{ margin: 0, fontSize: ".85rem", color: "var(--nx-state-stale-ink)" }}>
            {contato.fraseDeCompartilhamento}
          </p>
        )}
        <p style={{ margin: 0, fontSize: ".85rem", color: "var(--nx-mute)" }}>
          {contato.fraseDeConexao}
        </p>
      </div>

      {canOperate && (
        <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
          <h2 style={{ fontSize: "1rem" }}>Configuração operacional</h2>
          <form onSubmit={(event) => void saveConfiguration(event)} className="nx-filters" style={{ alignItems: "end" }}>
            <label className="nx-field">
              <span>Potência nominal (kW)</span>
              <input name="nominalPowerKw" type="number" min="1" max="2000" step="1" defaultValue={charger.nominalPowerKw ?? ""} placeholder="Ex.: 150" />
            </label>
            <button className="nx-btn" type="submit" disabled={savingConfiguration}>
              {savingConfiguration ? "Salvando…" : "Salvar potência"}
            </button>
          </form>
          <p style={{ color: "var(--nx-mute)", fontSize: ".8rem", marginBottom: 0 }}>
            Informe a potência de placa do carregador. Este valor é usado nos indicadores de utilização e eficiência.
          </p>
        </div>
      )}

      <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
        <h2 style={{ fontSize: "1rem" }}>Conectores</h2>
        {legendaDegradada !== null && (
          <p style={{ color: "var(--nx-mute)", fontSize: ".8rem", fontStyle: "italic" }}>
            {legendaDegradada}
          </p>
        )}
        {/*
          `connectorId > 0`, COMO NAS TRÊS TELAS IRMÃS — e como as DUAS listas de
          comando logo abaixo, neste mesmo arquivo, já faziam.

          Na OCPP 1.6 o `connectorId = 0` designa o PONTO DE RECARGA: é por onde
          passam `Reset`, `Heartbeat` e o `StatusNotification` da estação. Não é
          um bocal. Medido ao vivo em `/app/chargers/D24030483`: a tabela listava
          `#0 | Available`, `#1 | Charging`, `#2 | Available` — o ponto de
          recarga apresentado como terceiro conector, contra a decisão 1.1/1.2 do
          dono, e com a cardinalidade errada no lugar onde o operador conta
          bocais. A inconsistência era DENTRO do próprio arquivo.
        */}
        {conectoresFisicos.length === 0 ? (
          <p style={{ color: "var(--nx-mute)" }}>
            {/* "ainda" só vale quando esperar pode resolver. Para a fonte que
                declara `connector_topology` impossível, não pode. */}
            {fonteForneceCapacidade(charger, "connector_topology")
              ? "Nenhum conector reportado ainda."
              : `${charger.sourceLabel} não publica a topologia de conectores deste equipamento.`}
          </p>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Status</th>
                  <th scope="col">Código de erro</th>
                  <th scope="col">Info</th>
                  <th scope="col">Atualizado em</th>
                </tr>
              </thead>
              <tbody>
                {conectoresFisicos.map((c) => (
                  /* `connectorId` e não `id`: o contrato publicado não traz a
                     chave primária de `public.connectors`, e o `tsc` passou a
                     cobrar isso agora que a linha vem de `@nxvolt/shared`. */
                  <tr key={c.connectorId} style={legendaDegradada !== null ? { filter: "grayscale(0.6)" } : undefined}>
                    <td>#{c.connectorId}</td>
                    <td>{statusBadge(c.status)}</td>
                    {/*
                      `errorCode` e `info` SÃO OS `_shown` — a view os ANULA
                      quando a fonte declara os campos impossíveis, e o valor cru
                      fica ao lado, no banco, para a contradição ser
                      inspecionável. Um travessão aqui tem DUAS causas e elas
                      pedem ações diferentes: "a fonte não fornece" (não espere)
                      e "o conector não reportou erro" (bom sinal).
                    */}
                    <td>
                      {fonteForneceCapacidade(charger, "connector_error_code")
                        ? (c.errorCode ?? "sem erro reportado")
                        : "não fornecido pela fonte"}
                    </td>
                    <td>
                      {fonteForneceCapacidade(charger, "connector_info")
                        ? (c.info ?? "—")
                        : "não fornecido pela fonte"}
                    </td>
                    <td>{fmt(c.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/*
        O CARD INTEIRO SÓ EXISTE QUANDO HÁ COMANDO A DAR.

        Os quatro comandos dependem da MESMA capacidade (`remote_control`).
        Quando ela não existe (`motivoSemComando !== null`, caso de todo
        carregador Portal VGPL) o card SOME inteiro — título, botões e nota —
        em vez de mostrar um card titulado e oco. A ausência comunica melhor
        que não há comando a dar do que um cabeçalho sem controles.

        A caixa de texto que explicava a indisponibilidade foi removida a
        pedido do cliente (vitrine): expunha a arquitetura de origem do dado.
      */}
      {canOperate && motivoSemComando === null && (
        <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
          <h2 style={{ fontSize: "1rem" }}>Comandos remotos</h2>
          <div style={{ display: "flex", gap: "var(--nx-space-2)", flexWrap: "wrap" }}>
            <button className="nx-btn nx-btn--ghost-dark" type="button"
              disabled={busy !== null}
              onClick={() => void command("reset", { type: "Soft" }, "Reiniciar equipamento")}>
              {busy === "reset" ? "Enviando…" : "Reiniciar"}
            </button>
            {charger.connectors.filter((c) => c.connectorId > 0).map((c) => (
              <button key={`unlock-${c.connectorId}`} className="nx-btn nx-btn--ghost-dark" type="button"
                disabled={busy !== null}
                onClick={() => void command("unlock", { connectorId: c.connectorId }, `Destravar conector #${c.connectorId}`)}>
                {busy === "unlock" ? "Enviando…" : `Destravar #${c.connectorId}`}
              </button>
            ))}
            {charger.connectors.filter((c) => c.connectorId > 0 && c.status === "Available").map((c) => (
              <button key={`start-${c.connectorId}`} className="nx-btn" type="button"
                disabled={busy !== null}
                onClick={() => void command("remote-start", { connectorId: c.connectorId, idTag: "CARD-DEMO-001" }, `Iniciar recarga no conector #${c.connectorId}`)}>
                {busy === "remote-start" ? "Enviando…" : `Iniciar #${c.connectorId}`}
              </button>
            ))}
          </div>
          {/* A CAUSA da última falha, junto dos botões e anunciada. */}
          {falha !== null && (
            <p
              role="status"
              aria-live="polite"
              style={{
                color: "var(--nx-alert-ink)",
                background: "var(--nx-alert-bg)",
                border: "1px solid var(--nx-alert-ink)",
                borderRadius: 6,
                padding: "8px 10px",
                fontSize: ".85rem",
              }}
            >
              {falha}
            </p>
          )}
          <p style={{ color: "var(--nx-mute)", fontSize: ".8rem", marginBottom: 0 }}>
            Cada solicitação e sua resposta são registradas na auditoria. O equipamento pode recusar um comando por segurança ou capacidade OCPP.
          </p>
        </div>
      )}

      <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
        <h2 style={{ fontSize: "1rem" }}>Transações recentes</h2>
        {txs.length === 0 ? (
          <p style={{ color: "var(--nx-mute)" }}>
            {/*
              ESCOPO, e NÃO impossibilidade — os dois vocabulários não se
              misturam. As sessões EXISTEM na fonte de origem e não são
              sincronizadas nesta versão do portal; dizer "não há e não vai
              haver" aqui seria falso, e "sem transações" seco é indistinguível
              de "este carregador nunca foi usado".
            */}
            {fonteForneceCapacidade(charger, "transactions")
              ? "Sem transações registradas para este carregador."
              : fraseDeRelacaoVazia("transaction_visible", [charger.sourceLabel])}
          </p>
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th></th>
                  <th>#</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Energia</th>
                  <th>Motivo</th>
                  {canOperate && <th></th>}
                </tr>
              </thead>
              <tbody>
                {txs.map((t) => (
                  <tr
                    key={t.id}
                    onClick={() => setSelectedTx(t.ocppTransactionId)}
                    style={{
                      cursor: "pointer",
                      background:
                        selectedTx === t.ocppTransactionId
                          ? "var(--nx-bg-alt)"
                          : undefined,
                    }}
                  >
                    <td>
                      <input
                        type="radio"
                        name="tx"
                        checked={selectedTx === t.ocppTransactionId}
                        onChange={() => setSelectedTx(t.ocppTransactionId)}
                      />
                    </td>
                    <td className="mono">#{t.ocppTransactionId}</td>
                    <td>{fmt(t.startTime)}</td>
                    <td>{t.stopTime ? fmt(t.stopTime) : <em>em curso</em>}</td>
                    <td className="mono">
                      {t.energyWh == null
                        ? "—"
                        : (t.energyWh / 1000).toFixed(3) + " kWh"}
                    </td>
                    <td>{t.stopReason ?? "—"}</td>
                    {canOperate && (
                      <td>
                        {!t.stopTime && motivoSemComando === null && (
                          <button
                            className="nx-btn nx-btn--sm"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              void remoteStop(t.ocppTransactionId);
                            }}
                          >
                            Stop
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedTx != null && (
        <div className="nx-card" style={{ marginTop: "var(--nx-space-6)" }}>
          <h2 style={{ fontSize: "1rem" }}>
            Energia acumulada — transação #{selectedTx}{" "}
            {mvsImpossivel === null && (
              <small style={{ color: "var(--nx-mute)", fontWeight: 400 }}>
                ({mvs.length} medições)
              </small>
            )}
          </h2>
          {/*
            IMPOSSIBILIDADE, e não "poucas medições para plotar".

            `public.meter_values` só é escrita pelo servidor OCPP DESTE portal;
            a rota devolve 409 `SOURCE_UNSUPPORTED` para carregador de fonte que
            não a alimenta, com a frase pronta. Desenhar o eixo vazio e dizer
            "poucas medições" promete que esperar resolve — e aqui não resolve.
          */}
          {mvsImpossivel !== null ? (
            <p style={{ color: "var(--nx-mute)" }}>{mvsImpossivel}</p>
          ) : (
            <LineChart mvs={mvs} />
          )}
        </div>
      )}
    </div>
  );
}
